import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  COMPARAISON_MIN,
  GROUPE_POIDS,
  lignesComparaison,
  lireSelectionComparaison,
  meilleursDeLigne,
  type CandidatCompare,
  type Comparaison,
  type LigneComparaison,
  type ValeurComparee,
} from '@rh/shared';
import { appel } from '../api';
import { BoutonExport } from '../composants/BoutonExport';
import { Chargement, Erreur } from '../composants/Etat';
import { Etoile } from '../composants/Etoile';
import { Note, Pastille } from '../composants/Pastille';
import { classeNom, nomComplet } from '../format';

function afficher(valeur: ValeurComparee, format: LigneComparaison['format']): string {
  if (valeur === null) return '—';
  if (typeof valeur === 'number') {
    return valeur.toLocaleString(
      'fr-FR',
      format === 'note' ? { minimumFractionDigits: 1, maximumFractionDigits: 1 } : { maximumFractionDigits: 1 },
    );
  }
  return valeur;
}

/** Écran 5 — deux à quatre candidats côte à côte, ligne par ligne sur les mêmes axes. */
export function Comparateur() {
  const [parametres, setParametres] = useSearchParams();
  const brut = parametres.get('candidatures') ?? '';
  const selection = useMemo(() => lireSelectionComparaison(brut), [brut]);
  const ids = 'ids' in selection ? selection.ids : null;
  const cle = ids?.join(',') ?? '';

  const [comparaison, setComparaison] = useState<Comparaison | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [poids, setPoids] = useState(false);
  const titre = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    if (!cle) return;
    let actif = true;
    setErreur(null);
    appel<Comparaison>(`/comparateur?candidatures=${cle}`)
      .then((reponse) => {
        if (actif) setComparaison(reponse);
      })
      .catch((e: Error) => {
        if (actif) setErreur(e.message);
      });
    return () => {
      actif = false;
    };
  }, [cle]);

  if (!ids) {
    return (
      <section>
        <div className="entete-annonce">
          <h2>Comparateur</h2>
        </div>
        <p className="mention">
          {'erreur' in selection ? selection.erreur : ''}. Cochez « Comparer » sur les cartes d’une liste de candidatures.
        </p>
        <Link to="/annonces">Voir les annonces</Link>
      </section>
    );
  }

  // Un retrait se lit dans la comparaison déjà reçue ; un ajout attend la réponse.
  const trouves = ids.map((id) => comparaison?.candidats.find((c) => c.candidatureId === id));
  const candidats = trouves.every(Boolean) ? (trouves as CandidatCompare[]) : null;
  const lignes = comparaison
    ? lignesComparaison(comparaison.avecCoordonnees).filter((l) => poids || l.groupe !== GROUPE_POIDS)
    : [];
  const annonceCommune =
    candidats && candidats.every((c) => c.annonceId === candidats[0]!.annonceId) ? candidats[0]!.annonceId : null;

  function retirer(id: string): void {
    setParametres({ candidatures: ids!.filter((autre) => autre !== id).join(',') }, { replace: true });
    // Le bouton activé disparaît avec sa colonne : le focus revient au titre plutôt qu'à la page.
    titre.current?.focus();
  }

  return (
    <section className="comparateur">
      <div className="entete-annonce">
        <div>
          <h2 ref={titre} tabIndex={-1}>
            Comparateur
          </h2>
          {annonceCommune && (
            <Link to={`/annonces/${annonceCommune}`} className="lien-annonce">
              Retour à la liste
            </Link>
          )}
        </div>
        {candidats && (
          <BoutonExport chemin={`/exports/comparateur?candidatures=${cle}`} nomParDefaut="rh-comparaison.xlsx" />
        )}
      </div>
      <p className="mention">Meilleure valeur de chaque ligne en gras et en couleur.</p>

      {erreur && <Erreur message={erreur} />}
      {!candidats && !erreur && <Chargement quoi="de la comparaison" />}
      {candidats && (
        <>
          <div className="defilement">
            <table className="table-comparateur">
              <thead>
                <tr>
                  <th scope="col">Rubrique</th>
                  {candidats.map((c) => (
                    <th scope="col" key={c.candidatureId}>
                      <Etoile potentiel={c.potentiel} />
                      <Link to={`/candidatures/${c.candidatureId}`} className={classeNom(c.statut)}>
                        {nomComplet(c)}
                      </Link>
                      <span className="notes-comparees">
                        <Pastille valeur={c.potentiel} libelle />
                        <Note valeur={c.noteEntretien} date={c.evaluation?.evalueeLe ?? null} />
                      </span>
                      {candidats.length > COMPARAISON_MIN && (
                        <button
                          type="button"
                          className="lien"
                          onClick={() => retirer(c.candidatureId)}
                          aria-label={`Retirer ${nomComplet(c)} de la comparaison`}
                        >
                          Retirer
                        </button>
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {lignes.map((ligne, i) => {
                  const meilleurs = meilleursDeLigne(ligne, candidats);
                  return (
                    <Fragment key={ligne.cle}>
                      {(i === 0 || lignes[i - 1]!.groupe !== ligne.groupe) && (
                        <tr className="groupe-comparaison">
                          <th scope="colgroup" colSpan={candidats.length + 1}>
                            <span>{ligne.groupe}</span>
                          </th>
                        </tr>
                      )}
                      <tr>
                        <th scope="row">{ligne.libelle}</th>
                        {candidats.map((c, j) => {
                          const texte = afficher(ligne.valeur(c), ligne.format);
                          return (
                            <td key={c.candidatureId} className={ligne.format === 'long' ? 'long' : undefined}>
                              {meilleurs[j] ? (
                                <strong className="meilleur" title="Meilleure valeur de la ligne">
                                  {texte}
                                </strong>
                              ) : (
                                texte
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          <button type="button" className="lien" aria-expanded={poids} onClick={() => setPoids((v) => !v)}>
            {poids ? 'Masquer les poids du barème' : 'Afficher les poids du barème'}
          </button>
        </>
      )}
    </section>
  );
}
